import React, { useEffect, useState } from "react";
import { ILoadingProps } from "./Loading";
import { LoadingContainer } from "./LoadingContainer";
import { PointerEventsProperty } from "csstype";

export interface IFrameWithLoadingProps
    extends React.DetailedHTMLProps<
        React.IframeHTMLAttributes<HTMLIFrameElement>,
        HTMLIFrameElement
    > {
    /**
     * Properties for the Loading component used while this IFrame is loading.
     */

    loadingProps?: ILoadingProps;
    pointerEventsProperty: PointerEventsProperty;
}

/**
 * Iframe component which is wrapped in a loading container to display
 * the loading icon while the iframe is loading initially.
 * @param props Iframe properties with loading property composite.
 */
export const IFrameWithLoading = (
    props: IFrameWithLoadingProps,
): JSX.Element => {
    const [pixelRatio, setPixelRatio] = useState<number>(
        window.devicePixelRatio,
    );

    useEffect(() => {
        function handleResize(): void {
            setPixelRatio(window.devicePixelRatio);
        }

        window.addEventListener(`resize`, handleResize);

        return (): void => {
            window.removeEventListener(`resize`, handleResize);
        };
    }, []);

    const { pointerEventsProperty, style, src, ...rest } = props;

    const createOnLoadHandle =
        (setIsLoading: (isLoading: boolean) => void) =>
        (event: React.SyntheticEvent<HTMLIFrameElement>): void => {
            // Register user activity inside the iframe to avoid adding it to every tool individually.
            // We don't want to bubble the entire event as that could trigger other unintended events.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (event.target as HTMLIFrameElement).contentWindow.addEventListener(
                "mousemove",
                (window as any).__AUTH_UPDATE,
            );

            // Pass event through if neccessary.
            if (props.onLoad) {
                props.onLoad(event);
            }
            setIsLoading(false);
        };

    const scaledZoomHeight =
            pixelRatio !== 1
                ? Number(style.height) / pixelRatio - 100 / pixelRatio
                : Number(style.height),
        scaledZoomWidth =
            pixelRatio !== 1
                ? Number(style.width) / pixelRatio - 25 / pixelRatio
                : Number(style.width);

    return (
        <div
            style={{
                height: `${scaledZoomHeight}px`,
                pointerEvents: pointerEventsProperty,
                width: `${scaledZoomWidth}px`,
            }}
        >
            <LoadingContainer loadingProps={props.loadingProps}>
                {({ setIsLoading }): JSX.Element => (
                    <iframe
                        onLoad={createOnLoadHandle(setIsLoading)}
                        src={src}
                        style={{
                            border: style.border,
                            height: `${scaledZoomHeight}px`,
                            width: `${scaledZoomWidth}px`,
                        }}
                        {...rest}
                    />
                )}
            </LoadingContainer>
        </div>
    );
};
